/* globals data, leagueAverageDataFilteredGrouped, serverParams, visualSwings */
import Table from 'bam-node-savant-table/table-shim.ts'
import { guff } from 'bam-node-baseball-savant/utils/guff'

import '../swing-path/SwingPathApp.tsx'
import { populateUrl } from 'bam-node-baseball-savant/leaderboard/utils/leaderboard-filter-helpers'
import { TRIGGER_VIDEO_CHANGE } from '../swing-path/constants.ts'

import initStaticHeaders from '../utils/static-headers'
import concatGroupedHeaders from '../utils/concat-grouped-headers'
import getBatTrackingFirstHeaders from '../utils/bat-tracking-first-headers.ts'
import getGroupedHeaders from '../utils/get-grouped-headers'

const tableDivId = 'bat_tracking_table'
const isNotPlayer = new Set(['batting-team', 'pitching-team', 'league']).has(
    serverParams.type
)
const pitchHandRedundancy =
    serverParams.type === 'pitcher' &&
    serverParams.secondaryGroupBy.includes('pitch_hand')
const batSideRedundancy =
    serverParams.type === 'batter' &&
    serverParams.secondaryGroupBy.includes('bat_side')

const isLeague = Boolean(serverParams.type === 'league')

const hideSide = isNotPlayer || batSideRedundancy || pitchHandRedundancy

const { seasonStart, seasonEnd } = serverParams

const firstHeaders = getBatTrackingFirstHeaders(
    serverParams.type,
    serverParams.secondaryGroupBy
)

const headers = [
    // Player, Team, Side
    ...firstHeaders,
    {
        name: 'Avg. <br/>Bat Speed',
        field: 'avg_sweetspot_speed_mph',
        headerClass: `th-component-header align-center th-vertical-bottom border-left`,
        fieldClass: `tr-data align-center border-left`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'swing_speed_qualified'),
        toolTip: 'Competitive Swings Only',
        highlight: [62, 71, 80],
    },
    {
        name: 'Swing<br />Path<br />Tilt<a class="th-glossary-link" href="https://www.mlb.com/glossary/statcast/swing-path-tilt" target="_blank">[?]</a>',
        field: 'avg_plane_vertical_angle',
        headerClass: `th-component-header align-center th-vertical-bottom border-left dotted-right`,
        fieldClass: `tr-data align-center border-left dotted-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'bat_path_angle'),
        toolTip:
            'Vertical angle of swing<br />path over the 40 ms<br />prior to contact',
    },
    {
        name: 'Attack<br />Angle<a class="th-glossary-link" href="https://www.mlb.com/glossary/statcast/attack-angle" target="_blank">[?]</a>',
        field: 'attack_angle',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip:
            'Vertical angle at which<br />sweet spot is traveling<br />at the point of impact',
        displayFn: (val) => guff(val, 'bat_path_angle'),
    },
    {
        name: 'Attack<br />Direction<a class="th-glossary-link" href="https://www.mlb.com/glossary/statcast/attack-direction" target="_blank">[?]</a>',
        field: 'attack_direction_pullopp',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        toolTip:
            'Horizontal angle at which<br />sweet spot is traveling<br />at the point of impact',
        displayFn: (val) => {
            const pullOpp = val < 0 ? 'PULL' : 'OPP'
            const formattedVal = guff(Math.abs(val), 'bat_path_angle')
            return `${formattedVal}<span class="arm-glv-label">${pullOpp}</span>`
        },
    },
    {
        name: 'Ideal<br />Attack<br />Angle %<a class="th-glossary-link" href="https://www.mlb.com/glossary/statcast/ideal-attack-angle" target="_blank">[?]</a>',
        field: 'rate_ideal_attack_angle',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) =>
            guff(val, 'rate_ideal_attack_angle', { removePercent: true }),
        toolTip:
            'Percentage of swings<br />between 5-20° of<br />attack angle at contact',
        highlight: [0.1, 0.5, 0.9],
    },
    {
        name: 'vs. Front<br />of Plate',
        field: 'avg_intercept_y_vs_plate',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'dist_in_inches'),
        toolTip:
            'How far in front of the<br />front edge of home plate the<br />bat and ball cross',
    },
    {
        name: `vs. Batter<br />Center<br />of Mass`,
        field: 'avg_intercept_y_vs_batter',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'dist_in_inches'),
        toolTip: `How far in front of the<br />batter's center of mass the<br />bat and ball cross`,
    },
    {
        name: 'Depth<br />in Box',
        field: 'avg_batter_y_position',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'dist_in_inches'),
        toolTip: `How far behind the<br />front of home plate the<br />batter stands (measured by<br />center of mass)`,
    },
    {
        name: 'Distance<br />off Plate',
        field: 'avg_batter_x_position',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'dist_in_inches'),
        toolTip: `How far away from the<br />inside of home plate the<br />batter stands (measured by<br />center of mass)`,
    },
    {
        name: 'Distance<br />between<br />Feet (in.)',
        field: 'avg_foot_sep0',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => guff(val, 'dist_in_inches'),
    },
    {
        name: 'Stance<br />Angle',
        field: 'avg_foot_angle0',
        headerClass: `th-component-header align-center th-vertical-bottom border-right`,
        fieldClass: `tr-data align-center border-right`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => {
            let openClsd = val < 0 ? 'OPEN' : 'CLOSE'
            if (val === 0.0) {
                openClsd = ''
            }
            const guffedVal = guff(Math.abs(val), 'bat_path_angle')
            return `${guffedVal}<span class="arm-glv-label">${openClsd}</span>`
        },
    },
    {
        name: 'Competitive<br />Swings',
        field: 'n_swings',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        type: 'number',
        displayFn: (val) => Number(val).toLocaleString(),
    },
    {
        name: 'Visual',
        field: 'name',
        headerClass: `th-component-header align-center th-vertical-bottom`,
        fieldClass: `tr-data align-center`,
        defaultSortDir: 'desc',
        displayFn: (_, row) => {
            const { id, bat_side_formatted, year } = row

            const swings = visualSwings.filter((d) => {
                const hasYear = Boolean(year)
                const matchesYear =
                    (hasYear && d.year === year) ||
                    (!hasYear && d.year >= seasonStart && d.year <= seasonEnd)
                return (
                    d.playerId === id &&
                    matchesYear &&
                    (bat_side_formatted === 'S' ||
                        d.batSide === bat_side_formatted)
                )
            })

            // This is a rough heuristic. Multiple years will have issues
            if (swings.length > 1 && swings[0].batSide !== swings[1].batSide) {
                return swings
                    .slice(0, 2)
                    .sort((a, b) => (a.batSide > b.batSide ? 1 : -1))
                    .map(
                        (swing) =>
                            `<button class="test-viz btn btn-default" data-player-id="${swing.playerId}" data-season="${swing.year}" data-bat-side="${swing.batSide}">${swing.year} - ${swing.batSide}</button>`
                    )
                    .join('&nbsp;')
            }

            const swing = swings[0]
            if (swing) {
                return `<button class="test-viz btn btn-default" data-player-id="${swing.playerId}" data-season="${swing.year}" data-bat-side="${swing.batSide}">${swing.year} - ${swing.batSide}HB</button>`
            }

            return '<em>Not Available</em>'
        },
    },
]

// Make this smarter some day
if (serverParams.type !== 'batter') {
    headers.pop()
}

const groupHeaders = getGroupedHeaders(serverParams.secondaryGroupBy)

function getFirstColspan() {
    // the default
    let colspan = 4 - Number(hideSide) - Number(isLeague)

    // Add the extra group by columns
    colspan += groupHeaders.length

    return colspan
}

const secondaryHeaders = [
    {
        name: '',
        colspan: getFirstColspan(),
        headerClass: 'th-name-header',
    },
    {
        name: 'mph',
        colspan: 1,
        headerClass: 'th-title-header th-secondary-em',
    },
    {
        name: '',
        colspan: 3,
        headerClass: 'th-title-header',
    },
    {
        name: '5 - 20°<br />Attack Angle',
        colspan: 1,
        headerClass: 'th-title-header th-secondary-em',
    },
    {
        name: 'Intercept<br />Point (inches)',
        colspan: 2,
        headerClass: 'th-title-header',
    },
    {
        name: 'Batter<br />Position (inches)',
        colspan: 2,
        headerClass: 'th-title-header',
    },
    {
        name: 'Batting<br />Stance<a class="th-glossary-link" href="/visuals/batting-stance" target="_blank">[?]</a>',
        colspan: 2,
        headerClass: 'th-title-header',
    },
    {
        name: '',
        colspan: 1,
        headerClass: 'th-secondary-em',
    },
]

let showLeagueAverage = false
let table

function loadTable(filteredData, existingSort = null, existingSortDir = null) {
    const withPinnedData = [
        ...filteredData,
        ...leagueAverageDataFilteredGrouped.map((d) => ({
            ...d,
            pinned: true,
        })),
    ]

    const width = document.getElementById(tableDivId).offsetWidth
    let repeatHeader = false
    if (width <= 768) {
        repeatHeader = 15
    }

    const URLParams = new URLSearchParams(window.location.search)
    const defaultSort =
        existingSort || URLParams.get('sortColumn') || 'avg_sweetspot_speed_mph'
    const defaultSortDirection =
        existingSortDir || URLParams.get('sortDirection') || 'desc'

    const lastColumn = firstHeaders[firstHeaders.length - 1].field

    const pagination = withPinnedData.length > 500

    // eslint-disable-next-line no-new
    table = new Table({
        id: tableDivId,
        repeatHeader,
        headers: concatGroupedHeaders(headers, groupHeaders, lastColumn),
        secondaryHeaders,
        highlightFalsy: true,
        data: withPinnedData,
        hasPinned: true,
        defaultSort,
        defaultSortDirection,
        sortClass: 'column-sort',
        showRank: true,
        showPinned: showLeagueAverage,
        pagination: {
            enabled: pagination,
        },
        search: {
            enabled: true,
        },
        sortCallback(column, _, { state }) {
            const url = new URL(window.location)
            url.searchParams.set('sortColumn', column)
            url.searchParams.set('sortDirection', state)
            window.history.replaceState('', '', url.toString())
        },
    })

    initStaticHeaders(tableDivId, data)
}

loadTable(data)

// Add Event Listeners
document.getElementById(tableDivId).addEventListener('click', (e) => {
    if (e.target.matches('button.test-viz')) {
        const { playerId, season, batSide } = e.target.dataset
        const customEvent = new CustomEvent(TRIGGER_VIDEO_CHANGE, {
            detail: {
                playerId: Number(playerId),
                season: Number(season || seasonEnd),
                batSide,
            },
        })
        window.dispatchEvent(customEvent)
    }
})

const $leagueAverageToggle = document.getElementById(
    'btn-toggle-league-average'
)

$leagueAverageToggle?.addEventListener('click', () => {
    showLeagueAverage = !showLeagueAverage
    table.setShowPinned(showLeagueAverage)
})

document.getElementById('btn-update').addEventListener('click', () => {
    window.location = populateUrl()
})
